// ===== K-Tec Enterprise Admin Utilities =====

export const generateId = (): string => {
  return `KT-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
};

export const generateQuotationNumber = (): string => {
  const now = new Date();
  const prefix = 'QT';
  const date = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
  const seq = String(Math.floor(Math.random() * 9999) + 1).padStart(4, '0');
  return `${prefix}-${date}-${seq}`;
};

export const generateInvoiceNumber = (): string => {
  const now = new Date();
  const prefix = 'INV';
  const date = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
  const seq = String(Math.floor(Math.random() * 9999) + 1).padStart(4, '0');
  return `${prefix}-${date}-${seq}`;
};

export const formatDate = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const formatDateTime = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

export const formatCurrency = (amount: number): string => {
  return `Rs. ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatNumber = (num: number): string => {
  return num.toLocaleString('en-US');
};

export const calculateGST = (amount: number, rate: number = 15): number => {
  return amount * (rate / 100);
};

export const calculateDiscount = (amount: number, discount: number, type: 'percentage' | 'fixed'): number => {
  if (type === 'percentage') return amount * (discount / 100);
  return discount;
};

export const getRelativeTime = (date: string | Date): string => {
  const now = new Date();
  const d = new Date(date);
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(date);
};

export const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    inactive: 'bg-gray-50 text-gray-500 border-gray-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-600 border-red-200',
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    partial: 'bg-amber-50 text-amber-700 border-amber-200',
    overdue: 'bg-red-50 text-red-600 border-red-200',
    draft: 'bg-gray-50 text-gray-500 border-gray-200',
    converted: 'bg-purple-50 text-purple-700 border-purple-200',
    cancelled: 'bg-red-50 text-red-600 border-red-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    expired: 'bg-red-50 text-red-600 border-red-200',
    claimed: 'bg-amber-50 text-amber-700 border-amber-200',
    received: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    ordered: 'bg-blue-50 text-blue-700 border-blue-200',
    site_visit: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    quotation: 'bg-blue-50 text-blue-700 border-blue-200',
    installation: 'bg-amber-50 text-amber-700 border-amber-200',
    warranty: 'bg-purple-50 text-purple-700 border-purple-200',
  };
  return colors[status] || 'bg-gray-50 text-gray-500 border-gray-200';
};

export const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    site_visit: 'Site Visit',
    site_visit_completed: 'Site Visit Done',
  };
  return labels[status] || status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
};

export const classNames = (...classes: (string | boolean | undefined | null)[]): string => {
  return classes.filter(Boolean).join(' ');
};

export const debounce = <T extends (...args: unknown[]) => unknown>(fn: T, delay: number) => {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

export const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

// ────────────────────────────────────────────────────────────────
// Pagination utility
// ────────────────────────────────────────────────────────────────

export interface PaginationState {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  start: number;
  end: number;
  hasPrev: boolean;
  hasNext: boolean;
}

export function paginate<T>(items: T[], page: number, perPage: number): { items: T[]; pagination: PaginationState } {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * perPage;
  const end = Math.min(start + perPage, total);
  return {
    items: items.slice(start, end),
    pagination: {
      page: safePage,
      perPage,
      total,
      totalPages,
      start: total === 0 ? 0 : start + 1,
      end,
      hasPrev: safePage > 1,
      hasNext: safePage < totalPages,
    },
  };
}

// ────────────────────────────────────────────────────────────────
// Validation helpers
// ────────────────────────────────────────────────────────────────

export const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const isValidPhone = (phone: string): boolean =>
  /^[\d\s\+\-\(\)]{7,15}$/.test(phone);

export const isRequired = (value: string | number | null | undefined): boolean =>
  value !== null && value !== undefined && String(value).trim() !== '';
